/**
 * ‹  Newest first  ›
 *
 * The control Anthony settled on: two arrows, and the word between them is
 * itself the button. The arrows step one at a time for the common case —
 * next month, next sort — and the middle opens the whole list for when you
 * know where you are going. One control doing both jobs, rather than a
 * dropdown next to a pair of arrows doing half each.
 *
 * It is used for sorting on every list, and the same shape drives the month
 * on the calendar. That is the point of it being here rather than written
 * per page: the last system had a date range picker, a sort dropdown and a
 * month stepper that all looked different and behaved differently, and
 * nobody could predict any of them.
 */

import { esc } from "./dom.ts";

export type Option<T> = { key: T; label: string };

export type Picker<T> = {
  /** Drop into markup. */
  html: string;
  /**
   * Call after the markup is in the document.
   *
   * `onStep` turns the two arrows into a STEPPER: see the note on
   * `opts.step` below. Without it they cycle the option list, which is
   * what every sort on every list wants.
   */
  bind: (onChange: (key: T) => void, onStep?: (dir: number) => void) => void;
};

let seq = 0;

export function picker<T extends string>(
  options: Array<Option<T>>,
  current: T,
  /**
   * ─── WHAT THE ARROWS ARE FOR ─────────────────────────────────────────
   *
   * By default they cycle the option list, which is right for a sort:
   * next to "Newest first" is "Oldest first" and there is nothing else an
   * arrow could mean.
   *
   * It is wrong for a DATE RANGE, and it was the bug. Anthony: "if we
   * select today arrows to left and right it just switched
   * day/week/month/all time instead of switching between
   * yesterday/today/tomorrow or the right range based on what's selected
   * in the center selector." With Today showing, the two things next to
   * today are yesterday and tomorrow -- not "this week". The middle picks
   * the UNIT, the arrows move in it.
   *
   * `step: true` says so. The page then gets `onStep(dir)` and decides
   * what one step means, because only the page knows that a week is seven
   * days and a month is not.
   *
   * `nowLabel` lets the caller name the period being looked at rather
   * than the option that was chosen -- "Yesterday" where the option list
   * says "Today". Without it the control would keep saying Today while
   * showing Tuesday's numbers, which is the same lie in a new place.
   *
   * `canStep: false` greys the arrows. "All time" has nothing before it,
   * and an arrow that silently does nothing is worse than one that is
   * plainly off.
   */
  opts: { label?: string; step?: boolean; nowLabel?: string; canStep?: boolean } = {},
): Picker<T> {
  const id = `pk${++seq}`;
  const at = Math.max(0, options.findIndex((o) => o.key === current));
  const now = options[at] ?? options[0]!;
  const dead = opts.step === true && opts.canStep === false;

  const html = `
    <div class="picker" id="${id}">
      ${opts.label ? `<span class="picker-cap">${esc(opts.label)}</span>` : ""}
      <div class="picker-ctl">
        <button class="picker-arrow" data-step="-1" aria-label="Previous"${
          dead ? " disabled" : ""}>&#8249;</button>
        <button class="picker-now" aria-haspopup="true">${esc(opts.nowLabel ?? now.label)}</button>
        <button class="picker-arrow" data-step="1" aria-label="Next"${
          dead ? " disabled" : ""}>&#8250;</button>
        <div class="picker-menu" hidden>
          ${options.map((o, i) => `
            <button class="picker-opt${i === at ? " on" : ""}" data-i="${i}">${esc(o.label)}</button>`).join("")}
        </div>
      </div>
    </div>`;

  const bind = (onChange: (key: T) => void, onStep?: (dir: number) => void): void => {
    const root = document.getElementById(id);
    if (!root) return;
    const menu = root.querySelector<HTMLElement>(".picker-menu");

    const pick = (i: number) => {
      // Wraps on purpose. A stepper that stops at the end makes somebody
      // travel all the way back for the option next to where they started.
      const next = options[(i + options.length) % options.length];
      if (next) onChange(next.key);
    };

    root.querySelectorAll<HTMLButtonElement>(".picker-arrow").forEach((b) =>
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        const dir = Number(b.dataset["step"] ?? 0);
        // Asked for a stepper AND given somewhere to send it. A page that
        // passes `step: true` and forgets the handler falls back to
        // cycling rather than to a button that does nothing at all.
        if (opts.step && onStep) { if (!dead) onStep(dir); return; }
        pick(at + dir);
      }));

    root.querySelector<HTMLButtonElement>(".picker-now")?.addEventListener("click", (e) => {
      e.stopPropagation();
      if (menu) menu.hidden = !menu.hidden;
    });

    root.querySelectorAll<HTMLButtonElement>(".picker-opt").forEach((b) =>
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        pick(Number(b.dataset["i"] ?? 0));
      }));

    // Anywhere else closes it. Without this the menu survives a scroll and
    // sits over whatever you were trying to read.
    document.addEventListener("click", () => { if (menu) menu.hidden = true; }, { once: true });
  };

  return { html, bind };
}

/* ── the sorts every list shares ──────────────────────────────────────
   Same words in the same order everywhere, because "Newest first" meaning
   two different things on two screens is worse than having no sort. */

export type SortKey = "new" | "old" | "big" | "small" | "az";

export const SORTS: Array<Option<SortKey>> = [
  { key: "new", label: "Newest first" },
  { key: "old", label: "Oldest first" },
  { key: "big", label: "Largest first" },
  { key: "small", label: "Smallest first" },
  { key: "az", label: "By name" },
];

/** Which of the shared sorts a given list can actually honor. */
export const sortsFor = (keys: SortKey[]): Array<Option<SortKey>> =>
  SORTS.filter((s) => keys.includes(s.key));

/** Turned into the column and direction a query wants. */
export function orderFor(k: SortKey, opts: { date: string; amount?: string; name?: string }):
  { column: string; ascending: boolean } {
  switch (k) {
    case "old": return { column: opts.date, ascending: true };
    case "big": return { column: opts.amount ?? opts.date, ascending: false };
    case "small": return { column: opts.amount ?? opts.date, ascending: true };
    case "az": return { column: opts.name ?? opts.date, ascending: true };
    default: return { column: opts.date, ascending: false };
  }
}
